import { trpc } from "@/lib/trpc";
import { useChatStore } from "@/stores/chat-store";
import { toast } from "sonner";
import { fromError } from "zod-validation-error";

export const useSendMessageMutation = (setLoading?: (loading: boolean) => void, setError?: (error: string) => void) => {
    const { filters } = useChatStore();
    const utils = trpc.useUtils();
    return trpc.messages.create.useMutation({
        onMutate: async (variables) => {
            if (setLoading) setLoading(true);
            if (setError) setError("");
            utils.messages.findMany.cancel({ threadId: variables.threadId, ...filters });

            const previousMessages = utils.messages.findMany.getInfiniteData({
                ...filters,
                threadId: variables.threadId,
            });
            console.log(previousMessages);
            utils.messages.findMany.setInfiniteData(
                {
                    ...filters,
                    threadId: variables.threadId,
                },
                (oldData: any) => {
                    // 1) if there’s no cache yet, seed pages & pageParams
                    if (!oldData) {
                        return {
                            pages: [
                                {
                                    data: [variables], // your single new message
                                    count: 1,
                                    nextPage: undefined,
                                },
                            ],
                            pageParams: [null], // first pageParam can be `null` or `undefined`
                        };
                    }

                    // 2) Update only the first page
                    const updatedFirstPage = {
                        ...oldData.pages[0],
                        data: [variables, ...oldData.pages[0].data],
                        count: oldData.pages[0].count + 1,
                    };

                    // 3) Return the full InfiniteData shape
                    return {
                        pages: [updatedFirstPage, ...oldData.pages.slice(1)],
                        pageParams: oldData.pageParams,
                    };
                }
            );
            return { previousMessages };
        },
        onSuccess: (_, variables) => {
            utils.messages.findMany.invalidate({
                ...filters,
                threadId: variables.threadId,
            });
        },
        onError: (error, variables, context: any) => {
            toast.error(fromError(error).toString());
            utils.messages.findMany.setInfiniteData(
                {
                    ...filters,
                    threadId: variables.threadId,
                },
                context.previousMessages
            );
        },
        // onSettled: async (_,__,variables) => {
        //     utils.messages.findMany.invalidate({threadId:variables.threadId});

        // }
    });
};


export const useUpdateMessageMutation = (onSuccess?: () => void) => {
    const utils = trpc.useUtils();
    return trpc.messages.update.useMutation({
        onSuccess: async (data) => {
            utils.messages.findMany.invalidate();
            utils.messages.findOne.invalidate(data.id);
            onSuccess?.();
        },
    });
}

export const useAddFeedbackMutation = () => {
    const { filters } = useChatStore();
    const utils = trpc.useUtils();
    return trpc.messages.addFeedback.useMutation({
        onSuccess: async (data) => {
            utils.messages.findMany.invalidate(filters);
            utils.messages.findOne.invalidate(data.id);
        },
    })
}

export const useDeleteMessageMutation = () => {
    const { filters } = useChatStore();
    const utils = trpc.useUtils();
    return trpc.messages.delete.useMutation({
        onMutate: async (messageId) => {
            // Cancel any outgoing refetches
            await utils.messages.findMany.cancel();

            // Snapshot the previous value
            const previousMessages = utils.messages.findMany.getInfiniteData(filters);

            // Optimistically remove the message from cache
            utils.messages.findMany.setInfiniteData(filters, (oldData: any) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                        ...page,
                        data: page.data.filter((message: any) => message.id !== messageId),
                        count: page.count - 1,
                    })),
                };
            });

            return { previousMessages };
        },
        onError: (context: any) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previousMessages) {
                utils.messages.findMany.setInfiniteData(filters, context.previousMessages);
            }
            toast.error("Failed to delete message");
        },
        onSettled: () => {
            // Always refetch after error or success
            utils.messages.findMany.invalidate(filters);
        },
    });
}

export const useRegenerateMessageMutation = () => {
    const { filters } = useChatStore();
    const utils = trpc.useUtils();
    return trpc.messages.regenerate.useMutation({
        onSuccess: (variables) => {
            // Invalidate messages to show the new assistant message
            utils.messages.findMany.invalidate({
                ...filters,
                threadId: variables.threadId,
            });
        },
        onError: () => {
            toast.error("Failed to regenerate response");
        },
    });
}
